import * as React from 'react';
import dayjs from 'dayjs';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { getStopById } from 'Global/JourneyElements/Utils/getStopById';
import { appConfigStore, RefundMethod } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { formatCurrency, getDefaultCurrencySymbol } from 'Util/_HumanWritten/CurrencyUtils';
import { creditTermInline, creditTermLabel } from 'Util/_HumanWritten/ferryCreditTerm';
import { formatDurationValue, TimeUnit } from 'Util/TimeUnit';
import { stringNotEmpty } from 'Util/TypeGuards';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { WizardModal } from 'Views/Components/Modal/WizardModal';
import { NumberInput } from 'Views/Components/New/NumberInput';
import {
	BookingCancellationInformationDto,
	BookingCancellationRefundDto,
	canCancelMigratedBookingWithNoLimit,
	computeSelectedTotalForMethod,
	defaultRefundMethod,
	CancellationFeeGroup,
	getMethodBookingCutoff,
	getMethodFeeGroups,
	getMethodRefundAmount,
	isManualRefundOnly,
	RefundCardDto,
	RefundModel,
} from './NewCancelBookingModal';
import { validateRefundAmount } from './StaffModalContent';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { CancellationReasonPicker, cancellationReasonError } from './CancellationReasonPicker';
import {
	CancellationReasonOptionDto,
	getCancellationReasons,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';

type WizardStepId = 'select' | 'refund' | 'fee' | 'confirm';

// Floats from summing per-booking refund amounts can produce values like 12.345000001;
// snap to cents so the override input never shows or saves more than 2 decimals.
const roundCurrency = (n: number): number => Math.round(n * 100) / 100;

interface CancellationWizardProps {
	isOpen: boolean;
	cancellationInformation: BookingCancellationInformationDto;
	bookingLegacyId?: string;
	onClose: () => void;
	onConfirm: (refundModel: RefundModel, recommendedRefundAmount: number) => Promise<boolean | undefined>;
	// Skip step 1 (which trips to cancel) for flows that only ever cancel a single item.
	skipSelectionStep?: boolean;
	// Suppress per-item cancellation-rule rendering (used by event/parking flows where
	// cutoff rules don't apply). When true, step 2 is collapsed entirely if there's no
	// method selection or manager override to offer — there's nothing else to do on it.
	hideCancellationRules?: boolean;
	// When false, exclude Manual from the offered refund methods even if the actor's role
	// would normally see it. Used by parking, whose backend only supports Bank/Credit.
	manualRefundAllowed?: boolean;
	// Customise the modal heading. Defaults to "Cancel booking".
	wizardTitle?: string;
	// Soft warnings to display at the top of the wizard ("already departed", "checked in",
	// etc.). The user can still proceed — warnings are informational, not blocking.
	warnings?: string[];
	// Hard blockers — same shape as warnings but render as an error and prevent the wizard
	// from advancing past whatever step the user is on (Continue / Confirm are disabled).
	// Used to stop customers from cancelling a departed or checked-in booking even if the
	// outer Cancel button was somehow shown.
	blockers?: string[];
	// Identifies the thing being cancelled so the server can offer the reasons that apply to it. Ferry
	// and event flows pass the booking id and the server resolves the type; parking passes
	// bookingType='PARKING' because a reservation is not a BookingEntity.
	reasonBookingId?: string;
	reasonBookingType?: string;
	// Whether the booking being cancelled is a ferry booking. Drives the credit terminology:
	// ferry bookings show "Ferry credit", non-ferry (event/parking) show "Credit". Defaults true.
	isFerryBooking?: boolean;
	// Set once the cancellation has gone through, for flows that report the outcome inside the wizard
	// rather than on the booking-cancelled page (check-in). Replaces the steps with this content and a
	// single Done button — there is no Back, the booking is already cancelled. `onClose` is Done.
	result?: React.ReactNode;
}

export const CancellationWizard = observer(
	({
		isOpen,
		cancellationInformation,
		bookingLegacyId,
		onClose,
		onConfirm,
		skipSelectionStep = false,
		hideCancellationRules = false,
		manualRefundAllowed = true,
		wizardTitle = 'Cancel booking',
		warnings = [],
		blockers = [],
		isFerryBooking = true,
		reasonBookingId,
		reasonBookingType,
		result,
	}: CancellationWizardProps) => {
		const { bookings, invoiced, isUnpaid, discounted, cardsToRefund } = cancellationInformation;

		// "You" vs "the customer" — when staff are cancelling on someone else's behalf the copy
		// should refer to the customer; when the customer is self-cancelling it should be "you".
		const refundRecipientReceives = store.isStaff ? 'the customer receives' : 'you receive';

		// Neither automated method enabled — backend falls into the manual refund branch.
		const manualOnly = isManualRefundOnly();

		// Build the list of refund methods that should appear as cards. Manual is only ever
		// surfaced to staff (the customer flow keeps its existing Bank/Credit shape). When only
		// one method is offered, SingleMethodHighlight is rendered instead of the cards.
		const allowedMethods = appConfigStore.allowedRefundMethodsFor(store.isStaff);
		const offeredMethods: RefundMethod[] = [];
		if (allowedMethods.has('Bank')) offeredMethods.push('Bank');
		if (allowedMethods.has('Credit')) offeredMethods.push('Credit');
		if (allowedMethods.has('Manual') && store.isStaff && manualRefundAllowed) offeredMethods.push('Manual');

		// Credit voucher is the actor's only refund option. When true the wizard drops the "refund"
		// framing and presents the outcome as a ferry credit.
		const creditOnly = offeredMethods.length === 1 && offeredMethods[0] === 'Credit';

		// Override is gated on WaiveFees, the capability the server checks before honouring it. Pre-existing
		// exception: staff can override on migrated bookings where modification isn't otherwise possible -
		// the server applies no fee to those, so the entered refund stands without the capability.
		const canOverridePrice =
			store.can('WaiveFees') || (store.isStaff && canCancelMigratedBookingWithNoLimit(bookingLegacyId));

		// Fetched once when the wizard opens. Deliberately does not gate rendering: the picker lives on
		// the confirmation step, which the user cannot reach in the time this takes, and the step list
		// must not change shape mid-flow.
		const [reasonOptions, setReasonOptions] = React.useState<CancellationReasonOptionDto[]>([]);
		const [reasonRequired, setReasonRequired] = React.useState(false);
		const [showReasonErrors, setShowReasonErrors] = React.useState(false);
		const reasonPickerRef = React.useRef<HTMLDivElement>(null);

		React.useEffect(() => {
			if (!isOpen || (!reasonBookingId && !reasonBookingType)) {
				return;
			}
			let cancelled = false;
			getCancellationReasons({ bookingId: reasonBookingId, bookingType: reasonBookingType })
				.then(result => {
					if (cancelled) return;
					setReasonOptions(result.options ?? []);
					setReasonRequired(result.required ?? false);
				})
				.catch(() => {
					// A reason is a record-keeping field, not a precondition for cancelling. If the list
					// can't be loaded the picker stays hidden and the cancellation proceeds — the server
					// only rejects a missing reason when it actually had options to offer.
				});
			return () => {
				cancelled = true;
			};
		}, [isOpen, reasonBookingId, reasonBookingType]);

		const [step, setStep] = React.useState(1);

		const refundModelRef = React.useRef<RefundModel | null>(null);
		if (refundModelRef.current === null) {
			// Honour the tenant default, but never initialise to a method that the wizard isn't
			// offering (e.g. Manual when manualRefundAllowed is false).
			let initialMethod = defaultRefundMethod();
			if (offeredMethods.length > 0 && !offeredMethods.includes(initialMethod)) {
				initialMethod = offeredMethods[0];
			}
			const initialForceCreditRefund = initialMethod === 'Credit';
			const initialForceManualRefund = initialMethod === 'Manual';
			// When the selection step is skipped, treat every item as selected. Otherwise pre-select
			// only the mainBooking entries (the ferry-side flow lets the user opt the return leg in).
			const initialSelected = skipSelectionStep
				? bookings.map(b => b.id)
				: bookings.filter(b => b.mainBooking).map(b => b.id);
			const initialAmount = computeSelectedTotalForMethod(bookings, initialSelected, initialForceCreditRefund);
			refundModelRef.current = observable({
				refundAmount: roundCurrency(initialAmount),
				refundAmountErrors: '',
				selectedBookings: initialSelected,
				bookingRefunds: {},
				forceCreditRefund: initialForceCreditRefund,
				forceManualRefund: initialForceManualRefund,
			});
		}
		const refundModel = refundModelRef.current;

		// Round every aggregate to cents. Summing per-booking amounts can leave float tails (e.g.
		// 285.2129); the entered refund amount is already rounded, so comparing an unrounded recommended
		// figure against it would (a) make isOverrideEngaged fire when nothing changed and (b) print a
		// long-decimal "system recommended $285.2129" in the cancellation note.
		const selectedMaximumRefund = roundCurrency(
			bookings
				.filter(b => refundModel.selectedBookings.includes(b.id))
				.reduce((sum, b) => sum + b.maximumRefund, 0),
		);

		const systemRecommendedRefund = roundCurrency(
			computeSelectedTotalForMethod(bookings, refundModel.selectedBookings, refundModel.forceCreditRefund),
		);

		const totalFees = roundCurrency(selectedMaximumRefund - systemRecommendedRefund);
		const selectedBankRefund = roundCurrency(
			bookings
				.filter(b => refundModel.selectedBookings.includes(b.id))
				.reduce((sum, b) => sum + b.bankRecommendedRefundAmount, 0),
		);
		const selectedCreditRefund = roundCurrency(
			bookings
				.filter(b => refundModel.selectedBookings.includes(b.id))
				.reduce((sum, b) => sum + b.creditRecommendedRefundAmount, 0),
		);
		// A cutoff that wipes out the refund regardless of method (e.g. 100% fee on both).
		const fullCancellationFee = selectedMaximumRefund > 0 && selectedBankRefund === 0 && selectedCreditRefund === 0;
		const noRefundReason = resolveNoRefundReason({
			invoiced,
			isUnpaid,
			discounted,
			selectedMaximumRefund,
			fullCancellationFee,
		});
		// A 100% cancellation fee zeroes the system-recommended refund, but the booking still has
		// refundable funds (selectedMaximumRefund > 0). Override-capable actors (managers, and staff
		// on no-limit migrated bookings) must still be able to grant a discretionary refund here —
		// the old StaffModalContent flow allowed this via `maximumRefund > 0 && !invoiced`. Every
		// other no-refund reason (invoiced, unpaid, fully discounted, nothing refundable) genuinely
		// has no funds to give back, so those stay blocked even for managers. `resolveNoRefundReason`
		// only returns the fee message when not invoiced, so `fullCancellationFee && !invoiced`
		// isolates the fee-only block.
		const onlyBlockedByFullFee = fullCancellationFee && !invoiced;
		const canGiveRefund = !noRefundReason || (canOverridePrice && onlyBlockedByFullFee);
		const showMethodSelector = offeredMethods.length >= 2 && canGiveRefund;

		// Invoiced bookings charge a cancellation fee instead of refunding (nothing was paid). The
		// fee is shown directly from the server (it can't be derived from a refund), and managers can
		// edit it (0 waives it). selectedMaximumRefund equals the full fare for invoiced bookings, so
		// it doubles as the upper bound for the fee override.
		const invoicedFeeFlow = invoiced;
		// Sum the fee across the selected bookings (mirrors how the refund amounts are computed) so
		// deselecting a leg is reflected, rather than using the combined all-bookings total.
		const systemCancellationFee = roundCurrency(
			bookings
				.filter(b => refundModel.selectedBookings.includes(b.id))
				.reduce((sum, b) => sum + (b.cancellationFee ?? 0), 0),
		);
		const cancellationFeeMax = roundCurrency(selectedMaximumRefund);
		const effectiveCancellationFee = roundCurrency(refundModel.cancellationFeeOverride ?? systemCancellationFee);
		// Managers (and staff on no-limit migrated bookings) get a dedicated step to edit the fee.
		const invoicedManagerFeeStep = invoicedFeeFlow && canOverridePrice;
		const cancellationFeeError = (() => {
			if (!invoicedManagerFeeStep || refundModel.cancellationFeeOverride === undefined) return '';
			const value = refundModel.cancellationFeeOverride;
			if (value < 0) return 'Fee cannot be negative.';
			if (value > cancellationFeeMax + 0.001) {
				return `Fee cannot exceed ${formatCurrency(cancellationFeeMax)}.`;
			}
			return '';
		})();

		// Build the active step sequence. The refund-summary step has three reasons to exist:
		// the per-item cutoff/fee breakdown (ferry only), the method selector (≥2 methods), or
		// a manager override panel. Drop it entirely when none of those apply. Invoiced bookings never
		// show the refund step — they get the fee step instead (managers only).
		const refundStepHasContent =
			!invoicedFeeFlow && canGiveRefund && (showMethodSelector || canOverridePrice || !hideCancellationRules);
		const steps: WizardStepId[] = [];
		if (!skipSelectionStep) steps.push('select');
		if (refundStepHasContent) steps.push('refund');
		if (invoicedManagerFeeStep) steps.push('fee');
		steps.push('confirm');
		const totalSteps = steps.length;
		// Manager override is "engaged" only once the entered amount diverges from the
		// system-recommended figure. Drives the amber theming on confirm + step 3. For invoiced
		// bookings this keys off the fee override rather than the refund amount.
		const isOverrideEngaged = invoicedFeeFlow
			? invoicedManagerFeeStep &&
				refundModel.cancellationFeeOverride !== undefined &&
				Math.abs(effectiveCancellationFee - systemCancellationFee) > 0.001
			: canOverridePrice && canGiveRefund && Math.abs(refundModel.refundAmount - systemRecommendedRefund) > 0.001;

		const toggleBooking = action((id: string) => {
			const idx = refundModel.selectedBookings.indexOf(id);
			if (idx >= 0) {
				refundModel.selectedBookings.splice(idx, 1);
			} else {
				refundModel.selectedBookings.push(id);
			}
			refundModel.refundAmount = roundCurrency(
				computeSelectedTotalForMethod(bookings, refundModel.selectedBookings, refundModel.forceCreditRefund),
			);
			refundModel.refundAmountErrors = '';
		});

		const selectMethod = action((method: RefundMethod) => {
			const nextForceCredit = method === 'Credit';
			const nextForceManual = method === 'Manual';
			if (refundModel.forceCreditRefund === nextForceCredit && refundModel.forceManualRefund === nextForceManual)
				return;
			refundModel.forceCreditRefund = nextForceCredit;
			refundModel.forceManualRefund = nextForceManual;
			// Reset to system-recommended for the new method whenever the user toggles so any
			// pending manager override doesn't carry over to a different fee context. Manual
			// uses the bank-equivalent quote (forceCreditRefund=false).
			refundModel.refundAmount = roundCurrency(
				computeSelectedTotalForMethod(bookings, refundModel.selectedBookings, nextForceCredit),
			);
			refundModel.refundAmountErrors = '';
		});

		// Clamp the persisted step counter to the active step list — the list can shrink between
		// renders (e.g. user deselects everything in step 1 → noRefundReason → 2 total steps).
		const stepIndex = Math.min(step, totalSteps) - 1;
		const currentStepId = steps[stepIndex];

		// When the manager reaches the invoiced fee step, commit the system-recommended fee as the
		// starting override so the amount shown on the fee/confirm steps is exactly what gets charged —
		// rather than leaving the override undefined and relying on the server's implicit fallback to the
		// calculated fee when the field is never touched. Only seeds when untouched (override === undefined),
		// so a manager's explicit edit is preserved; seeding to the system value keeps isOverrideEngaged off.
		React.useEffect(() => {
			if (currentStepId === 'fee' && refundModel.cancellationFeeOverride === undefined) {
				runInAction(() => {
					refundModel.cancellationFeeOverride = systemCancellationFee;
				});
			}
		}, [currentStepId, systemCancellationFee, refundModel]);

		const canContinueSelectStep = refundModel.selectedBookings.length > 0;
		const canContinueRefundStep =
			!canOverridePrice ||
			!canGiveRefund ||
			(stringNotEmpty(refundModel.refundAmountErrors) === false && refundModel.refundAmount >= 0);

		const goNext = () => {
			if (currentStepId === 'refund' && canOverridePrice && canGiveRefund) {
				const isValid = validateRefundAmount(refundModel, selectedMaximumRefund, bookingLegacyId);
				if (!isValid) return;
			}
			if (currentStepId === 'fee' && cancellationFeeError !== '') return;
			setStep(s => Math.min(totalSteps, s + 1));
		};

		const goBack = () => {
			const current = stepIndex + 1;
			if (current === 1) {
				onClose();
				return;
			}
			setStep(current - 1);
		};

		const [isSubmitting, setIsSubmitting] = React.useState(false);

		const reasonError = cancellationReasonError(reasonOptions, reasonRequired, refundModel);

		const handleConfirm = async () => {
			if (isSubmitting) return;
			if (reasonError !== '') {
				// Surface the field error rather than failing silently on a disabled button, and scroll to
				// it: the picker sits at the bottom of the confirmation step, so on a short viewport the
				// error can render entirely below the fold and the button just looks broken.
				setShowReasonErrors(true);
				// After the error has rendered, so scrollIntoView measures the taller element.
				requestAnimationFrame(() => {
					reasonPickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
				});
				return;
			}
			if (canOverridePrice && canGiveRefund) {
				const isValid = validateRefundAmount(refundModel, selectedMaximumRefund, bookingLegacyId);
				if (!isValid) return;
			}
			if (invoicedManagerFeeStep && cancellationFeeError !== '') return;
			setIsSubmitting(true);
			try {
				await onConfirm(refundModel, systemRecommendedRefund);
			} finally {
				setIsSubmitting(false);
			}
		};

		const effectiveStep = stepIndex + 1;
		const isLastStep = currentStepId === 'confirm';
		const hasBlockers = blockers.length > 0;
		const continueDisabled =
			hasBlockers ||
			(currentStepId === 'select' && !canContinueSelectStep) ||
			(currentStepId === 'refund' && !canContinueRefundStep) ||
			(currentStepId === 'fee' && cancellationFeeError !== '');

		const footer = (
			<>
				<Button newStyles display={Display.Outline} colors={Colors.Grey} sizes={Sizes.Large} onClick={goBack}>
					{effectiveStep === 1 ? 'Close' : 'Back'}
				</Button>
				{!isLastStep ? (
					<Button
						newStyles
						display={Display.Solid}
						colors={Colors.Secondary}
						sizes={Sizes.Large}
						disabled={continueDisabled}
						onClick={goNext}
						buttonProps={{ 'data-test-id': 'cancellation-wizard-continue' } as any}
					>
						Continue
					</Button>
				) : (
					<Button
						newStyles
						display={Display.Solid}
						colors={isOverrideEngaged ? Colors.Warning : Colors.Secondary}
						sizes={Sizes.Large}
						onClick={handleConfirm}
						disabled={isSubmitting || hasBlockers}
						buttonProps={{ 'data-test-id': 'cancellation-wizard-confirm' } as any}
					>
						{isSubmitting ? (
							<span className="inline-flex items-center gap-2">
								<Icon name="progress_activity" useMaterialIcons classname="animate-spin" />
								Cancelling…
							</span>
						) : (
							'Yes, cancel booking'
						)}
					</Button>
				)}
			</>
		);

		if (result) {
			return (
				<WizardModal
					isOpen={isOpen}
					label="Booking cancelled"
					title="Booking cancelled"
					step={totalSteps}
					totalSteps={totalSteps}
					stepLabel="Complete"
					onClose={onClose}
					footer={
						<Button
							newStyles
							display={Display.Solid}
							colors={Colors.Secondary}
							sizes={Sizes.Large}
							onClick={onClose}
							buttonProps={{ 'data-test-id': 'cancellation-wizard-done' } as any}
						>
							Done
						</Button>
					}
				>
					<div className="cancellation-wizard-result">{result}</div>
				</WizardModal>
			);
		}

		return (
			<WizardModal
				isOpen={isOpen}
				label={wizardTitle}
				title={wizardTitle}
				step={effectiveStep}
				totalSteps={totalSteps}
				onClose={onClose}
				footer={footer}
			>
				{currentStepId === 'select' && (
					<StepSelectBookings
						bookings={bookings}
						selectedIds={refundModel.selectedBookings}
						onToggle={toggleBooking}
					/>
				)}
				{currentStepId === 'refund' && (
					<StepRefundSummary
						bookings={bookings}
						refundModel={refundModel}
						showMethodSelector={showMethodSelector}
						offeredMethods={offeredMethods}
						canOverridePrice={canOverridePrice}
						onSelectMethod={selectMethod}
						systemRecommendedRefund={systemRecommendedRefund}
						selectedMaximumRefund={selectedMaximumRefund}
						refundRecipientReceives={refundRecipientReceives}
						manualOnly={manualOnly}
						creditOnly={creditOnly}
						isFerryBooking={isFerryBooking}
						hideCancellationRules={hideCancellationRules}
					/>
				)}
				{currentStepId === 'fee' && (
					<StepInvoicedFee
						refundModel={refundModel}
						systemCancellationFee={systemCancellationFee}
						cancellationFeeMax={cancellationFeeMax}
						error={cancellationFeeError}
					/>
				)}
				{currentStepId === 'confirm' && (
					<StepConfirmation
						bookings={bookings}
						selectedBookingIds={refundModel.selectedBookings}
						forceCreditRefund={refundModel.forceCreditRefund}
						finalRefundAmount={refundModel.refundAmount}
						systemRecommendedRefund={systemRecommendedRefund}
						selectedMaximumRefund={selectedMaximumRefund}
						totalFees={totalFees}
						isOverrideEngaged={isOverrideEngaged}
						canGiveRefund={canGiveRefund}
						manualPresentation={manualOnly || refundModel.forceManualRefund}
						isFerryBooking={isFerryBooking}
						noRefundReason={noRefundReason}
						cardsToRefund={cardsToRefund}
						invoicedFeeFlow={invoicedFeeFlow}
						cancellationFee={effectiveCancellationFee}
					/>
				)}
				{currentStepId === 'confirm' && (
					// Rendered alongside StepConfirmation rather than inside it: that component has three
					// separate return paths (invoiced fee, no refund, and the refund hero), and the picker
					// has to appear on all three.
					<CancellationReasonPicker
						options={reasonOptions}
						required={reasonRequired}
						refundModel={refundModel}
						showErrors={showReasonErrors}
						containerRef={reasonPickerRef}
					/>
				)}
				{(warnings.length > 0 || blockers.length > 0) && (
					<div className="mt-4 space-y-3">
						{blockers.length > 0 && <WizardBlockerBanner blockers={blockers} />}
						{warnings.length > 0 && <WizardWarningBanner warnings={warnings} />}
					</div>
				)}
			</WizardModal>
		);
	},
);

function WizardWarningBanner({ warnings }: { warnings: string[] }) {
	return (
		<MessageBox
			status="warning"
			slim
			smallText
			noBottomMargin
			title={warnings.length > 1 ? 'Notice — please review' : 'Notice'}
		>
			{warnings.length === 1 ? (
				<div>{warnings[0]}</div>
			) : (
				<ul className="list-disc pl-4 space-y-0.5">
					{warnings.map(w => (
						<li key={w}>{w}</li>
					))}
				</ul>
			)}
		</MessageBox>
	);
}

function WizardBlockerBanner({ blockers }: { blockers: string[] }) {
	return (
		// `block` rather than the status default: this is a rule stopping the action, not a failure.
		<MessageBox
			status="error"
			icon="block"
			slim
			smallText
			noBottomMargin
			title={
				blockers.length > 1
					? 'This booking can no longer be cancelled:'
					: 'This booking can no longer be cancelled.'
			}
		>
			{blockers.length === 1 ? (
				<div>{blockers[0]} Please contact staff if you need assistance.</div>
			) : (
				<>
					<ul className="list-disc pl-4 space-y-0.5">
						{blockers.map(b => (
							<li key={b}>{b}</li>
						))}
					</ul>
					<div className="mt-1">Please contact staff if you need assistance.</div>
				</>
			)}
		</MessageBox>
	);
}

// --- Step 1 ---

interface StepSelectBookingsProps {
	bookings: BookingCancellationRefundDto[];
	selectedIds: string[];
	onToggle: (id: string) => void;
}

const StepSelectBookings = observer(({ bookings, selectedIds, onToggle }: StepSelectBookingsProps) => {
	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-lg font-semibold text-slate-900 m-0">Which trips are you cancelling?</h3>
				<div className="text-sm text-slate-500 mt-1">Select the bookings you would like to cancel.</div>
			</div>
			<div className="space-y-3">
				{bookings.map(booking => {
					const selected = selectedIds.includes(booking.id);
					const subtitle = resolveBookingSubtitle(booking);
					return (
						<button
							type="button"
							key={booking.id}
							onClick={() => onToggle(booking.id)}
							disabled={booking.mainBooking}
							title={booking.mainBooking ? 'This booking must be cancelled' : undefined}
							data-test-id={
								booking.mainBooking
									? 'cancellation-wizard-booking-main'
									: 'cancellation-wizard-booking-linked'
							}
							className={`w-full text-left p-4 border rounded-xl transition-all ${
								selected
									? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500'
									: 'border-slate-200 hover:border-slate-300 bg-white'
							} ${booking.mainBooking ? 'cursor-default' : 'cursor-pointer'}`}
						>
							<div className="flex items-center gap-4">
								<span
									className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors shrink-0 ${
										selected ? 'bg-sky-500 border-sky-500' : 'bg-white border-slate-300'
									}`}
									aria-label={booking.mainBooking ? 'Required, cannot be deselected' : undefined}
								>
									{booking.mainBooking ? (
										<Icon name="lock" useMaterialIcons classname="text-white !text-[14px]" />
									) : (
										selected && (
											<Icon name="check" useMaterialIcons classname="text-white !text-[14px]" />
										)
									)}
								</span>
								<div className="flex flex-col min-w-0">
									<span className="font-semibold text-slate-800 truncate">
										<BookingRouteLabel booking={booking} />
									</span>
									{subtitle && <span className="text-[11px] text-slate-500 mt-0.5">{subtitle}</span>}
								</div>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
});

function BookingRouteLabel({ booking }: { booking: BookingCancellationRefundDto }) {
	if (booking.displayLabel) return <>{booking.displayLabel}</>;
	const start = getStopById(booking.startStopId)?.location?.name ?? 'Start';
	const end = getStopById(booking.endStopId)?.location?.name ?? 'End';
	return (
		<>
			{start} to {end}
		</>
	);
}

function resolveBookingSubtitle(booking: BookingCancellationRefundDto): string | null {
	if (booking.displaySubtitle) return booking.displaySubtitle;
	return formatDepartureLabel(booking.departureDateTime);
}

function formatDepartureLabel(departureDateTime: string | Date | null | undefined): string | null {
	if (!departureDateTime) return null;
	const m = dayjs(departureDateTime);
	if (!m.isValid()) return null;
	return `${m.format('ddd D MMM YYYY')}, ${formatStandardTime(departureDateTime)}`;
}

function formatCutoffRule(cutoff: BookingCancellationRefundDto['bookingCutoff']): string {
	if (!cutoff) return 'No cancellation fee applies.';
	const fee = (cutoff.staticFee ?? false) ? formatCurrency(cutoff.feeAmount as number) : `${cutoff.feeAmount}%`;
	// An any-time rule has no window: matching ignores its notice period, and the form hides the
	// field, so the stored value is whatever was last saved before the any-time box was ticked.
	// Printing it as "within N of departure" named a window the fee is in fact charged outside of,
	// and left the one thing the customer needs to know — that cancelling earlier does not avoid it
	// — unsaid.
	if (cutoff.appliesAnytime) {
		return `${fee} fee when cancelled at any time before departure.`;
	}
	const timeframe = formatDurationValue(
		cutoff.timeBeforeDepartureValue as number,
		(cutoff.timeBeforeDepartureUnit as TimeUnit) ?? TimeUnit.HOURS,
	);
	return `${fee} fee when cancelled within ${timeframe} of departure.`;
}

/**
 * How one fare type's rule reads on its own line — "100% fee", "$15.00 fee", "No cancellation fee".
 *
 * <p>Shorter than <see cref="formatCutoffRule"/> deliberately. The window is stated once above the
 * list, and repeating "when cancelled at any time before departure" on every line buries the one
 * thing that differs between them.</p>
 */
function formatGroupRule(group: CancellationFeeGroup): string {
	if (group.feeAmount === null || group.feeAmount === undefined) {
		return 'No cancellation fee';
	}
	return group.staticFee ? `${formatCurrency(group.feeAmount)} fee` : `${group.feeAmount}% fee`;
}

/**
 * Whether one sentence would misdescribe this booking's fee.
 *
 * <p>A booking priced one fare type at a time can still be described by a single rule — four
 * passengers all charged 100% is a 100% cancellation however many groups the pricing walked. The
 * breakdown is worth the space only when the groups actually disagree, which is what the customer
 * cannot work out from the total: a fee of $42.99 against a $102.98 booking reads as an arbitrary
 * number beside a line claiming 100%, until the premium fare that is charged nothing is named.</p>
 *
 * <p>Compared on the shape of the rule rather than its contribution, because two fare types on the
 * same 100% rule contribute different money purely by being priced differently — that is not a
 * disagreement worth four extra lines.</p>
 */
function needsFareTypeBreakdown(groups: CancellationFeeGroup[]): boolean {
	if (groups.length < 2) {
		return false;
	}
	const shapes = new Set(groups.map(g => `${g.staticFee}:${g.feeAmount ?? 'none'}`));
	return shapes.size >= 2;
}

/** One line per fare type: who it covers, what rule it was charged under, and what it added. */
function FareTypeFeeBreakdown({ groups }: { groups: CancellationFeeGroup[] }) {
	return (
		<div className="pt-1 space-y-1" data-test-id="cancellation-fare-type-breakdown">
			{groups.map((group, index) => (
				<div
					key={`${group.passengerClassName ?? 'none'}-${index}`}
					className="flex justify-between items-baseline gap-3 text-[11px] text-slate-500"
				>
					<span className="min-w-0 truncate">
						{group.passengerClassName ?? 'Standard'} ·{' '}
						{group.passengerCount === 1 ? '1 passenger' : `${group.passengerCount} passengers`}
					</span>
					<span className="shrink-0">
						{formatGroupRule(group)}
						{group.contribution > 0 && ` · -${formatCurrency(group.contribution)}`}
					</span>
				</div>
			))}
		</div>
	);
}

interface NoRefundReasonInputs {
	invoiced: boolean;
	isUnpaid: boolean;
	discounted?: boolean;
	selectedMaximumRefund: number;
	fullCancellationFee: boolean;
}

function resolveNoRefundReason({
	invoiced,
	isUnpaid,
	discounted,
	selectedMaximumRefund,
	fullCancellationFee,
}: NoRefundReasonInputs): string | null {
	if (invoiced) {
		return (
			'This booking was invoiced, so no automatic refund will be issued. ' +
			'If the invoice has already been paid, please contact staff to arrange a refund.'
		);
	}
	// `<= 0` rather than `=== 0` on every amount test below. A negative maximum refund is a data
	// artefact (see the floor in CalculateSummaryPrices), but if one ever reaches here it must read
	// as "nothing to refund" rather than slipping past an equality check: at a negative maximum
	// every guard returned null, so the wizard offered a refund step whose override input had an
	// upper bound below zero and a `>= 0` lower bound — an empty range no entry could satisfy.
	if (isUnpaid && selectedMaximumRefund <= 0) {
		return 'This booking is unpaid. There are no funds to refund.';
	}
	if (discounted && selectedMaximumRefund <= 0) {
		return 'This booking was fully discounted. There are no funds to refund.';
	}
	if (fullCancellationFee) {
		return 'A 100% cancellation fee applies to this booking. No refund will be issued.';
	}
	if (selectedMaximumRefund <= 0) {
		return 'No refundable amount remains on this booking.';
	}
	return null;
}

// --- Step 2 ---

interface StepRefundSummaryProps {
	bookings: BookingCancellationRefundDto[];
	refundModel: RefundModel;
	showMethodSelector: boolean;
	offeredMethods: RefundMethod[];
	canOverridePrice: boolean;
	onSelectMethod: (method: RefundMethod) => void;
	systemRecommendedRefund: number;
	selectedMaximumRefund: number;
	refundRecipientReceives: string;
	manualOnly: boolean;
	creditOnly: boolean;
	isFerryBooking: boolean;
	hideCancellationRules: boolean;
}

const StepRefundSummary = observer(
	({
		bookings,
		refundModel,
		showMethodSelector,
		offeredMethods,
		canOverridePrice,
		onSelectMethod,
		systemRecommendedRefund,
		selectedMaximumRefund,
		refundRecipientReceives,
		manualOnly,
		creditOnly,
		isFerryBooking,
		hideCancellationRules,
	}: StepRefundSummaryProps) => {
		const selected = bookings.filter(b => refundModel.selectedBookings.includes(b.id));

		let summarySubtitle = '';
		if (creditOnly) {
			// Credit-only cancellations present as a credit; no extra refund framing needed.
			summarySubtitle = '';
		} else if (showMethodSelector) {
			summarySubtitle = `The refund amount changes based on how ${refundRecipientReceives} the funds.`;
		} else if (manualOnly) {
			summarySubtitle = store.isStaff
				? 'Staff will process this refund manually after the booking is cancelled.'
				: 'Staff will contact you to process this refund after the booking is cancelled.';
		} else if (refundModel.forceCreditRefund) {
			summarySubtitle = `Refund will be issued as a ${creditTermInline(isFerryBooking)} for future travel.`;
		} else {
			summarySubtitle = 'Refund will be returned to the original payment method.';
		}

		let summaryTitle: string;
		if (showMethodSelector) {
			summaryTitle = 'Choose refund method';
		} else if (creditOnly) {
			summaryTitle = 'Summary';
		} else {
			summaryTitle = 'Refund summary';
		}

		return (
			<div className="space-y-6">
				<div>
					<h3 className="text-lg font-semibold text-slate-900 m-0">{summaryTitle}</h3>
					{summarySubtitle && <div className="text-sm text-slate-500 mt-1">{summarySubtitle}</div>}
				</div>

				{showMethodSelector ? (
					<MethodSelectorCards
						selectedMethod={resolveSelectedMethod(refundModel)}
						offeredMethods={offeredMethods}
						onSelect={onSelectMethod}
						bankFee={computeSelectedFeeForMethod(selected, false)}
						creditFee={computeSelectedFeeForMethod(selected, true)}
						isFerryBooking={isFerryBooking}
					/>
				) : (
					<SingleMethodHighlight
						forceCreditRefund={refundModel.forceCreditRefund}
						manualOnly={manualOnly}
						isFerryBooking={isFerryBooking}
					/>
				)}

				<div className="space-y-3">
					<div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Selected bookings</div>
					{selected.map(booking => (
						<BookingBreakdownCard
							key={booking.id}
							booking={booking}
							forceCreditRefund={refundModel.forceCreditRefund}
							hideCancellationRules={hideCancellationRules}
						/>
					))}
				</div>

				{canOverridePrice && (
					<StaffOverridePanel
						refundModel={refundModel}
						systemRecommendedRefund={systemRecommendedRefund}
						selectedMaximumRefund={selectedMaximumRefund}
					/>
				)}
			</div>
		);
	},
);

interface BookingBreakdownCardProps {
	booking: BookingCancellationRefundDto;
	forceCreditRefund: boolean;
	hideCancellationRules: boolean;
}

// Per-booking row on step 2. Ferries get a collapsible breakdown (Booking price /
// Cancellation fee / rule); event + parking flows pass hideCancellationRules=true and the
// card renders as a static compact row without the expander.
function BookingBreakdownCard({ booking, forceCreditRefund, hideCancellationRules }: BookingBreakdownCardProps) {
	const refundAmount = getMethodRefundAmount(booking, forceCreditRefund);
	const cutoff = getMethodBookingCutoff(booking, forceCreditRefund);
	const feeGroups = hideCancellationRules ? [] : getMethodFeeGroups(booking, forceCreditRefund);
	const showGroups = needsFareTypeBreakdown(feeGroups);
	// The blanket sentence is the dearest-contributing rule, which describes the whole fee only when
	// every fare type was charged the same way. Where they were not, it is replaced rather than
	// printed above the breakdown that contradicts it.
	const ruleLabel = hideCancellationRules || showGroups ? null : formatCutoffRule(cutoff);
	const subtitle = resolveBookingSubtitle(booking);
	const fee = booking.maximumRefund - refundAmount;
	const canExpand = !hideCancellationRules;
	const [expanded, setExpanded] = React.useState(false);

	const header = (
		<div className="flex justify-between items-start gap-3">
			<div className="min-w-0 flex-1">
				<div className="font-semibold text-slate-800 text-sm">
					<BookingRouteLabel booking={booking} />
				</div>
				{subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
			</div>
			<div className="flex items-center gap-2 shrink-0">
				<span className="font-bold text-slate-900">{formatCurrency(refundAmount)}</span>
				{canExpand && (
					<Icon
						name={expanded ? 'expand_less' : 'expand_more'}
						useMaterialIcons
						classname="text-slate-400 !text-[18px]"
					/>
				)}
			</div>
		</div>
	);

	return (
		<div className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
			{canExpand ? (
				<button
					type="button"
					onClick={() => setExpanded(e => !e)}
					aria-expanded={expanded}
					className="w-full p-4 text-left hover:bg-slate-100 transition-colors"
				>
					{header}
				</button>
			) : (
				<div className="p-4">{header}</div>
			)}

			{canExpand && expanded && (
				<div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-white space-y-1.5">
					<div className="flex justify-between items-baseline text-sm">
						<span className="text-slate-600">Booking price</span>
						<span className="font-semibold text-slate-900">{formatCurrency(booking.maximumRefund)}</span>
					</div>
					{fee > 0 && (
						<div className="flex justify-between items-baseline text-sm">
							<span className="text-slate-600">Cancellation fee</span>
							<span className="font-semibold text-red-500">-{formatCurrency(fee)}</span>
						</div>
					)}
					{ruleLabel && <div className="text-[11px] text-slate-500 pt-1">{ruleLabel}</div>}
					{showGroups && <FareTypeFeeBreakdown groups={feeGroups} />}
				</div>
			)}
		</div>
	);
}

// Total cancellation fee across the selected bookings for a given refund method.
function computeSelectedFeeForMethod(
	selectedBookings: BookingCancellationRefundDto[],
	forceCreditRefund: boolean,
): number {
	const raw = selectedBookings.reduce((sum, b) => {
		const refundAmount = getMethodRefundAmount(b, forceCreditRefund);
		return sum + Math.max(0, b.maximumRefund - refundAmount);
	}, 0);
	return roundCurrency(raw);
}

function resolveSelectedMethod(model: RefundModel): RefundMethod {
	if (model.forceManualRefund) return 'Manual';
	if (model.forceCreditRefund) return 'Credit';
	return 'Bank';
}

interface MethodSelectorCardsProps {
	selectedMethod: RefundMethod;
	offeredMethods: RefundMethod[];
	onSelect: (method: RefundMethod) => void;
	bankFee: number;
	creditFee: number;
	isFerryBooking: boolean;
}

function MethodSelectorCards({
	selectedMethod,
	offeredMethods,
	onSelect,
	bankFee,
	creditFee,
	isFerryBooking,
}: MethodSelectorCardsProps) {
	// "Lowest" comparison only ranks bank vs credit (the automated paths). Manual uses the
	// bank-equivalent quote but is never highlighted as "lower fee" since staff settle it
	// offline — the fee equivalence isn't a customer-facing benefit.
	let lowestFee: 'Bank' | 'Credit' | null = null;
	const bankOffered = offeredMethods.includes('Bank');
	const creditOffered = offeredMethods.includes('Credit');
	if (bankOffered && creditOffered && Math.abs(bankFee - creditFee) > 0.001) {
		lowestFee = bankFee < creditFee ? 'Bank' : 'Credit';
	}

	const gridClass = offeredMethods.length === 3 ? 'grid grid-cols-3 gap-3' : 'grid grid-cols-2 gap-4';

	return (
		<div className={gridClass}>
			{offeredMethods.map(method => {
				if (method === 'Bank') {
					return (
						<MethodCard
							key="Bank"
							selected={selectedMethod === 'Bank'}
							iconName="account_balance"
							title="Bank refund"
							caption="Refund to original payment method"
							feeLabel={formatFeeLabel(bankFee, lowestFee === 'Bank')}
							highlightGreen={isGreenFee(bankFee, lowestFee === 'Bank')}
							onClick={() => onSelect('Bank')}
							testId="cancellation-wizard-method-Bank"
						/>
					);
				}
				if (method === 'Credit') {
					return (
						<MethodCard
							key="Credit"
							selected={selectedMethod === 'Credit'}
							iconName="redeem"
							title={creditTermLabel(isFerryBooking)}
							caption={`Issue a ${creditTermInline(isFerryBooking)} instead`}
							feeLabel={formatFeeLabel(creditFee, lowestFee === 'Credit')}
							highlightGreen={isGreenFee(creditFee, lowestFee === 'Credit')}
							onClick={() => onSelect('Credit')}
							testId="cancellation-wizard-method-Credit"
						/>
					);
				}
				// Manual uses the bank-equivalent quote, so it shares the bank fee. It never
				// gets the "Only $X fee" callout (that's reserved for the lower automated
				// path), but a $0 fee still renders as green "No fee" for consistency.
				return (
					<MethodCard
						key="Manual"
						selected={selectedMethod === 'Manual'}
						iconName="support_agent"
						title="Manual refund"
						caption="Staff settle the refund offline"
						feeLabel={formatFeeLabel(bankFee, false)}
						highlightGreen={isGreenFee(bankFee, false)}
						onClick={() => onSelect('Manual')}
						testId="cancellation-wizard-method-Manual"
					/>
				);
			})}
		</div>
	);
}

function formatFeeLabel(fee: number, isLowestFee: boolean): string {
	if (fee <= 0.001) return 'No fee';
	if (isLowestFee) return `Only ${formatCurrency(fee)} fee`;
	return `${formatCurrency(fee)} fee`;
}

function isGreenFee(fee: number, isLowestFee: boolean): boolean {
	return fee <= 0.001 || isLowestFee;
}

interface MethodCardProps {
	selected: boolean;
	iconName: string;
	title: string;
	caption: string;
	feeLabel: string;
	highlightGreen: boolean;
	onClick: () => void;
	testId?: string;
}

function MethodCard({
	selected,
	iconName,
	title,
	caption,
	feeLabel,
	highlightGreen,
	onClick,
	testId,
}: MethodCardProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`p-4 rounded-xl border text-left transition-all ${
				selected
					? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500'
					: 'border-slate-200 bg-white hover:border-slate-300'
			}`}
			aria-pressed={selected}
			data-test-id={testId}
		>
			<Icon
				name={iconName}
				useMaterialIcons
				classname={`text-2xl mb-2 block ${selected ? 'text-sky-600' : 'text-slate-400'}`}
			/>
			<div className="font-bold text-slate-900 text-sm">{title}</div>
			<div className="text-[10px] text-slate-500 mt-0.5 leading-snug">{caption}</div>
			<div className={`text-sm font-semibold mt-2 ${highlightGreen ? 'text-emerald-700' : 'text-slate-900'}`}>
				{feeLabel}
			</div>
		</button>
	);
}

function SingleMethodHighlight({
	forceCreditRefund,
	manualOnly,
	isFerryBooking,
}: {
	forceCreditRefund: boolean;
	manualOnly: boolean;
	isFerryBooking: boolean;
}) {
	let iconName: string;
	let title: string;
	let caption: string;

	if (manualOnly) {
		iconName = 'support_agent';
		title = 'Manual refund';
		caption = store.isStaff
			? 'Staff will process this refund manually after the booking is cancelled.'
			: 'Staff will contact you to process this refund manually.';
	} else if (forceCreditRefund) {
		iconName = 'redeem';
		title = creditTermLabel(isFerryBooking);
		caption = store.isStaff
			? 'A credit will be added to the customer’s account to re-book.'
			: 'A Credit will appear on your account to re-book.';
	} else {
		iconName = 'account_balance';
		title = 'Bank refund';
		caption = store.isStaff
			? 'Funds will be returned to the customer’s original payment method.'
			: 'Funds will be returned to your original payment method.';
	}

	return (
		<div className="bg-sky-50 border border-sky-100 rounded-xl p-4 flex items-center gap-4">
			<div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-sky-600 shadow-sm">
				<Icon name={iconName} useMaterialIcons classname="text-2xl" />
			</div>
			<div>
				<div className="font-bold text-sky-900 text-sm">{title}</div>
				<div className="text-[11px] text-sky-700">{caption}</div>
			</div>
		</div>
	);
}

interface StaffOverridePanelProps {
	refundModel: RefundModel;
	systemRecommendedRefund: number;
	selectedMaximumRefund: number;
}

const StaffOverridePanel = observer(
	({ refundModel, systemRecommendedRefund, selectedMaximumRefund }: StaffOverridePanelProps) => {
		const handleAmountChange = action((value: number | undefined) => {
			refundModel.refundAmount = roundCurrency(value ?? 0);
			validateRefundAmount(refundModel, selectedMaximumRefund, '');
		});

		return (
			<div
				className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3"
				data-test-id="cancellation-wizard-manager-override"
			>
				<div className="flex items-center gap-2 text-slate-700 font-bold text-sm uppercase tracking-tight">
					<Icon name="lock" useMaterialIcons classname="text-sm" />
					<span>Manager override</span>
				</div>

				<NumberInput
					id="cancellation-refund-textfield"
					label="Refund amount"
					value={refundModel.refundAmount}
					onChange={handleAmountChange}
					error={stringNotEmpty(refundModel.refundAmountErrors) ? refundModel.refundAmountErrors : undefined}
					placeholder={systemRecommendedRefund.toFixed(2)}
					allowDecimals
					allowNegative={false}
					startAdornment={getDefaultCurrencySymbol()}
				/>

				<div className="flex justify-between items-center text-xs text-slate-700 pt-1 border-t border-slate-200">
					<div className="flex flex-col">
						<span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
							Recommended
						</span>
						<span className="font-semibold">{formatCurrency(systemRecommendedRefund)}</span>
					</div>
					<div className="flex flex-col text-right">
						<span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Maximum</span>
						<span className="font-semibold">{formatCurrency(selectedMaximumRefund)}</span>
					</div>
				</div>
			</div>
		);
	},
);

// --- Step: invoiced cancellation fee (manager edit) ---

interface StepInvoicedFeeProps {
	refundModel: RefundModel;
	systemCancellationFee: number;
	cancellationFeeMax: number;
	error: string;
}

// Shown for invoiced bookings, which charge a cancellation fee instead of refunding. Managers can
// edit the fee directly (0 waives it). There is deliberately no refund amount/method here — nothing
// was paid, so there is nothing to refund.
const StepInvoicedFee = observer(
	({ refundModel, systemCancellationFee, cancellationFeeMax, error }: StepInvoicedFeeProps) => {
		const currentFee = refundModel.cancellationFeeOverride ?? systemCancellationFee;

		const handleFeeChange = action((value: number | undefined) => {
			refundModel.cancellationFeeOverride = roundCurrency(value ?? 0);
		});

		return (
			<div className="space-y-6">
				<div>
					<h3 className="text-lg font-semibold text-slate-900 m-0">Cancellation fee</h3>
					<div className="text-sm text-slate-500 mt-1">
						This booking was invoiced, so no refund applies. The customer will be charged this cancellation
						fee, which is added to their invoice.
					</div>
				</div>

				<div
					className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3"
					data-test-id="cancellation-wizard-fee-override"
				>
					<div className="flex items-center gap-2 text-slate-700 font-bold text-sm uppercase tracking-tight">
						<Icon name="lock" useMaterialIcons classname="text-sm" />
						<span>Manager override — cancellation fee</span>
					</div>

					<NumberInput
						id="cancellation-fee-textfield"
						label="Cancellation fee"
						value={currentFee}
						onChange={handleFeeChange}
						error={stringNotEmpty(error) ? error : undefined}
						placeholder={systemCancellationFee.toFixed(2)}
						allowDecimals
						allowNegative={false}
						startAdornment={getDefaultCurrencySymbol()}
					/>

					<div className="flex justify-between items-center text-xs text-slate-700 pt-1 border-t border-slate-200">
						<div className="flex flex-col">
							<span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
								Recommended
							</span>
							<span className="font-semibold">{formatCurrency(systemCancellationFee)}</span>
						</div>
						<div className="flex flex-col text-right">
							<span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
								Maximum
							</span>
							<span className="font-semibold">{formatCurrency(cancellationFeeMax)}</span>
						</div>
					</div>

					<div className="text-[11px] text-slate-500">Set the fee to {formatCurrency(0)} to waive it.</div>
				</div>
			</div>
		);
	},
);

// --- Step 3 ---

interface StepConfirmationProps {
	bookings: BookingCancellationRefundDto[];
	selectedBookingIds: string[];
	forceCreditRefund: boolean;
	finalRefundAmount: number;
	systemRecommendedRefund: number;
	selectedMaximumRefund: number;
	totalFees: number;
	isOverrideEngaged: boolean;
	canGiveRefund: boolean;
	// True when the confirmation should present as a manual refund — either the tenant only
	// allows manual (legacy path) or the user explicitly picked Manual on the card selector.
	manualPresentation: boolean;
	isFerryBooking: boolean;
	noRefundReason: string | null;
	cardsToRefund?: RefundCardDto[];
	// Invoiced bookings charge a cancellation fee instead of refunding; render a fee-focused
	// confirmation rather than the refund/no-refund layouts.
	invoicedFeeFlow: boolean;
	cancellationFee: number;
}

function StepConfirmation({
	bookings,
	selectedBookingIds,
	forceCreditRefund,
	finalRefundAmount,
	systemRecommendedRefund,
	selectedMaximumRefund,
	totalFees,
	isOverrideEngaged,
	canGiveRefund,
	manualPresentation,
	isFerryBooking,
	noRefundReason,
	cardsToRefund,
	invoicedFeeFlow,
	cancellationFee,
}: StepConfirmationProps) {
	const selected = bookings.filter(b => selectedBookingIds.includes(b.id));

	// Invoiced bookings: show the cancellation fee being charged (no refund concept).
	if (invoicedFeeFlow) {
		return (
			<InvoicedFeeConfirmation
				bookings={selected}
				cancellationFee={cancellationFee}
				isOverrideEngaged={isOverrideEngaged}
			/>
		);
	}

	// Route to the no-refund layout when no money is going back to the customer — either
	// the booking flat-out isn't refundable (!canGiveRefund), or an override-capable actor
	// reached this step with $0 entered. The latter is the 100% cancellation fee path: the
	// override panel is reachable so canGiveRefund is true, but if the manager doesn't enter
	// an amount the confirmation shouldn't hint at a refund being issued.
	if (!canGiveRefund || finalRefundAmount <= 0.001) {
		return <NoRefundConfirmation bookings={selected} noRefundReason={noRefundReason} />;
	}

	const heroAccent = resolveHeroAccent(isOverrideEngaged, forceCreditRefund, manualPresentation);
	const heroIcon = resolveHeroIcon(isOverrideEngaged, forceCreditRefund, manualPresentation);
	const accentStyles = HERO_ACCENT_STYLES[heroAccent];
	const expiryLabel = manualPresentation
		? null
		: resolveExpiryLabel(forceCreditRefund, appConfigStore.creditVoucherExpiryYears);
	const heading = resolveConfirmHeading(isOverrideEngaged, forceCreditRefund, manualPresentation);
	const subheading = resolveConfirmSubheading(
		isOverrideEngaged,
		forceCreditRefund,
		manualPresentation,
		isFerryBooking,
	);
	const footnote = resolveConfirmFootnote(isOverrideEngaged, forceCreditRefund, manualPresentation, isFerryBooking);

	// Pre-compute the card allocation so the hero block + the breakdown stay in sync. When
	// cards can't cover the full refund, surface the manual fallback in the Method label too
	// instead of just buried below ("Original card" → "Original card + manual").
	// `cardsToRefund === undefined` means the caller didn't fetch card info (event/parking flows
	// build their DTO locally without it), so we can't draw conclusions about a manual
	// fallback — assume the chosen method is what will happen and skip the breakdown.
	// Null is treated the same: it is what the server sends before the cards have been fetched.
	const hasCardData = cardsToRefund != null;
	const allocation = hasCardData
		? allocateCardRefunds(cardsToRefund, finalRefundAmount)
		: { cards: [], manualAmount: 0 };
	const isBankRefund = !manualPresentation && !forceCreditRefund;
	const hasManualFallback = isBankRefund && hasCardData && allocation.manualAmount > 0.001;

	let methodLabel: string;
	if (manualPresentation) {
		methodLabel = 'Manual refund';
	} else if (forceCreditRefund) {
		methodLabel = creditTermLabel(isFerryBooking);
	} else if (hasManualFallback && allocation.cards.length === 0) {
		methodLabel = 'Manual refund';
	} else if (hasManualFallback) {
		methodLabel = 'Original card + manual';
	} else {
		methodLabel = 'Original card';
	}

	let heroLabel: string;
	if (isOverrideEngaged) {
		heroLabel = 'New refund amount';
	} else if (forceCreditRefund && !manualPresentation) {
		heroLabel = `Total ${creditTermInline(isFerryBooking)}`;
	} else {
		heroLabel = 'Total refund amount';
	}

	// The "new fee" under manager override is the gap between what could have been refunded
	// at most and what is actually being refunded — i.e. the override-adjusted fee.
	const appliedFee = Math.max(0, selectedMaximumRefund - finalRefundAmount);
	const recommendedFee = Math.max(0, totalFees);

	const showCardSplit = hasCardData && !manualPresentation && !forceCreditRefund && finalRefundAmount > 0;

	return (
		<div className="space-y-6 text-center py-4">
			<div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${accentStyles.iconBg}`}>
				<Icon name={heroIcon} useMaterialIcons classname="text-3xl" />
			</div>
			<div>
				<h3 className="text-xl font-bold text-slate-900 m-0">{heading}</h3>
				<div className="text-slate-500 mt-2">{subheading}</div>
			</div>

			<div className={`rounded-2xl p-6 border ${accentStyles.cardBg}`}>
				{isOverrideEngaged ? (
					<div className="flex justify-center items-end gap-8 flex-wrap">
						<div>
							<div className={`text-xs font-bold uppercase tracking-widest mb-1 ${accentStyles.label}`}>
								{heroLabel}
							</div>
							<div className={`text-4xl font-black ${accentStyles.amount}`}>
								{formatCurrency(finalRefundAmount)}
							</div>
						</div>
						<div>
							<div className={`text-xs font-bold uppercase tracking-widest mb-1 ${accentStyles.label}`}>
								New fee
							</div>
							<div className={`text-4xl font-black ${accentStyles.amount}`}>
								{formatCurrency(appliedFee)}
							</div>
						</div>
					</div>
				) : (
					<>
						<div className={`text-xs font-bold uppercase tracking-widest mb-1 ${accentStyles.label}`}>
							{heroLabel}
						</div>
						<div className={`text-4xl font-black ${accentStyles.amount}`}>
							{formatCurrency(finalRefundAmount)}
						</div>
					</>
				)}
				<div
					className={`mt-4 pt-4 border-t flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-medium ${accentStyles.divider}`}
				>
					<div className="flex flex-col">
						<span className="text-[10px] uppercase opacity-70">Method</span>
						{methodLabel}
					</div>
					{isOverrideEngaged ? (
						<>
							<div className="flex flex-col">
								<span className="text-[10px] uppercase opacity-70">Recommended</span>
								{formatCurrency(systemRecommendedRefund)}
							</div>
							<div className="flex flex-col">
								<span className="text-[10px] uppercase opacity-70">Recommended fee</span>
								{formatCurrency(recommendedFee)}
							</div>
						</>
					) : (
						<>
							{expiryLabel && (
								<div className="flex flex-col">
									<span className="text-[10px] uppercase opacity-70">Expiry</span>
									{expiryLabel}
								</div>
							)}
							<div className="flex flex-col">
								<span className="text-[10px] uppercase opacity-70">Applied fee</span>
								{formatCurrency(recommendedFee)}
							</div>
						</>
					)}
				</div>
			</div>

			{showCardSplit && <CardRefundBreakdown allocation={allocation} />}

			<MessageBox status="warning" smallText noBottomMargin className="text-left">
				<div>{footnote}</div>
			</MessageBox>
		</div>
	);
}

interface NoRefundConfirmationProps {
	bookings: BookingCancellationRefundDto[];
	noRefundReason: string | null;
}

function NoRefundConfirmation({ bookings, noRefundReason }: NoRefundConfirmationProps) {
	return (
		<div className="space-y-5">
			<div className="text-center">
				<div className="w-16 h-16 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center mx-auto">
					<Icon name="info" useMaterialIcons classname="text-3xl" />
				</div>
				<h3 className="text-xl font-bold text-slate-900 m-0 mt-4">Confirm cancellation</h3>
				<div className="text-slate-500 mt-2">No refund will be issued for this cancellation.</div>
			</div>

			{noRefundReason && (
				<MessageBox status="warning" noBottomMargin>
					<div>{noRefundReason}</div>
				</MessageBox>
			)}

			<div className="space-y-3">
				<div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Selected bookings</div>
				{bookings.map(booking => {
					const subtitle = resolveBookingSubtitle(booking);
					return (
						<div key={booking.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
							<div className="font-semibold text-slate-800 text-sm">
								<BookingRouteLabel booking={booking} />
							</div>
							{subtitle && <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div>}
						</div>
					);
				})}
			</div>

			<div className="flex items-start gap-3 bg-blue-50 p-4 rounded-xl text-xs text-blue-800 text-left">
				<Icon name="info" useMaterialIcons classname="shrink-0 mt-0.5" />
				<div>By confirming, the booking will be cancelled. This cannot be undone once completed.</div>
			</div>
		</div>
	);
}

interface InvoicedFeeConfirmationProps {
	bookings: BookingCancellationRefundDto[];
	cancellationFee: number;
	isOverrideEngaged: boolean;
}

// Confirmation for invoiced bookings: charges a cancellation fee instead of refunding. Shows the
// fee that will be added to the customer's invoice broken down per booking (with the cutoff rule
// that drives each fee, mirroring the paid-cancellation breakdown), or that no fee applies (£0 /
// waived). A combined total is only shown when more than one leg is being cancelled.
function InvoicedFeeConfirmation({ bookings, cancellationFee, isOverrideEngaged }: InvoicedFeeConfirmationProps) {
	const hasFee = cancellationFee > 0.001;
	const accent = isOverrideEngaged
		? {
				iconBg: 'bg-amber-100 text-amber-600',
				cardBg: 'bg-amber-50 border-amber-200',
				label: 'text-amber-700',
				amount: 'text-amber-900',
			}
		: {
				iconBg: 'bg-slate-100 text-slate-600',
				cardBg: 'bg-slate-50 border-slate-200',
				label: 'text-slate-500',
				amount: 'text-slate-900',
			};

	// The backend distributes a manager override across legs proportionally to each leg's
	// calculated fee (DistributeFeeProportionally), so mirror that here to keep the displayed
	// per-booking amounts summing to the overridden total.
	const systemTotal = roundCurrency(bookings.reduce((sum, b) => sum + (b.cancellationFee ?? 0), 0));
	const perBookingFee = (booking: BookingCancellationRefundDto): number => {
		const systemFee = booking.cancellationFee ?? 0;
		if (!isOverrideEngaged) return roundCurrency(systemFee);
		if (systemTotal > 0.001) return roundCurrency((systemFee / systemTotal) * cancellationFee);
		// Manager imposed a fee where the calculated default was £0 — split it evenly across legs.
		return roundCurrency(cancellationFee / bookings.length);
	};
	const multipleBookings = bookings.length > 1;

	return (
		<div className="space-y-6 py-4">
			<div className="text-center space-y-2">
				<div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${accent.iconBg}`}>
					<Icon name={hasFee ? 'receipt_long' : 'check_circle'} useMaterialIcons classname="text-3xl" />
				</div>
				<h3 className="text-xl font-bold text-slate-900 m-0">Confirm cancellation</h3>
				<div className="text-slate-500">
					{hasFee
						? `The cancellation fee below will be added to ${store.isStaff ? 'the customer’s' : 'your'} invoice.`
						: 'No cancellation fee will be charged.'}
				</div>
			</div>

			<div className="space-y-3">
				<div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
					{multipleBookings ? 'Cancellation fees' : 'Cancellation fee'}
				</div>
				{bookings.map(booking => {
					const subtitle = resolveBookingSubtitle(booking);
					const fee = perBookingFee(booking);
					const showFee = fee > 0.001;
					// A manager override no longer reflects the cutoff rule, so only surface the rule
					// when the system-calculated fee is what's being charged.
					const feeGroups = !isOverrideEngaged && showFee ? booking.feeGroups ?? [] : [];
					const showGroups = needsFareTypeBreakdown(feeGroups);
					const ruleLabel =
						!isOverrideEngaged && showFee && !showGroups
							? formatCutoffRule(booking.bookingCutoff)
							: null;
					return (
						<div key={booking.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
							<div className="flex justify-between items-start gap-3">
								<div className="min-w-0 flex-1">
									<div className="font-semibold text-slate-800 text-sm">
										<BookingRouteLabel booking={booking} />
									</div>
									{subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
								</div>
								<span
									className={`font-bold shrink-0 ${showFee ? accent.amount : 'text-slate-400'}`}
									data-test-id="cancellation-wizard-fee-amount"
								>
									{showFee ? formatCurrency(fee) : 'No fee'}
								</span>
							</div>
							{ruleLabel && (
								<div className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-200">
									{ruleLabel}
								</div>
							)}
							{showGroups && (
								<div className="mt-2 pt-2 border-t border-slate-200">
									<FareTypeFeeBreakdown groups={feeGroups} />
								</div>
							)}
						</div>
					);
				})}

				{multipleBookings && hasFee && (
					<div className={`flex justify-between items-center rounded-xl p-4 border ${accent.cardBg}`}>
						<span className={`text-xs font-bold uppercase tracking-widest ${accent.label}`}>
							{isOverrideEngaged ? 'New total cancellation fee' : 'Total cancellation fee'}
						</span>
						<span className={`text-2xl font-black ${accent.amount}`}>
							{formatCurrency(cancellationFee)}
						</span>
					</div>
				)}
			</div>

			<div className="flex items-start gap-3 bg-blue-50 p-4 rounded-xl text-xs text-blue-800 text-left">
				<Icon name="info" useMaterialIcons classname="shrink-0 mt-0.5" />
				<div>By confirming, the booking will be cancelled. This cannot be undone once completed.</div>
			</div>
		</div>
	);
}

interface CardAllocation {
	cards: Array<{ card: RefundCardDto; amount: number }>;
	manualAmount: number;
}

// Allocates a bank refund across the available cards (largest capacity first). Any
// shortfall becomes a manual-refund portion that staff settle offline. Mirrors the
// behaviour of the older CardInformation component.
function allocateCardRefunds(cardsToRefund: RefundCardDto[] | undefined, refundAmount: number): CardAllocation {
	const cards = (cardsToRefund ?? [])
		.filter(c => (c.totalRefundableAmount ?? 0) > 0)
		.sort((a, b) => (b.totalRefundableAmount ?? 0) - (a.totalRefundableAmount ?? 0));

	const allocations: Array<{ card: RefundCardDto; amount: number }> = [];
	let remaining = refundAmount;
	for (const card of cards) {
		if (remaining <= 0) break;
		const take = Math.min(remaining, card.totalRefundableAmount ?? 0);
		if (take > 0) {
			allocations.push({ card, amount: take });
			remaining -= take;
		}
	}
	return {
		cards: allocations,
		manualAmount: Math.max(0, remaining),
	};
}

interface CardRefundBreakdownProps {
	allocation: CardAllocation;
}

function CardRefundBreakdown({ allocation }: CardRefundBreakdownProps) {
	const hasManual = allocation.manualAmount > 0.001;
	if (allocation.cards.length === 0 && !hasManual) return null;

	return (
		<div className="text-left space-y-2">
			<div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Refund destination</div>
			<div className="space-y-2">
				{allocation.cards.map(({ card, amount }) => (
					<div
						key={`${card.cardBrand ?? 'card'}-${card.last4 ?? ''}`}
						className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl p-3"
					>
						<span className="flex items-center gap-2 text-sm font-medium text-slate-800">
							<Icon name="credit_card" useMaterialIcons classname="text-slate-500" />
							{formatCardLabel(card)}
						</span>
						<span className="text-sm font-bold text-slate-900">{formatCurrency(amount)}</span>
					</div>
				))}
				{hasManual && (
					<div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-3">
						<span className="flex items-center gap-2 text-sm font-medium text-amber-900">
							<Icon name="support_agent" useMaterialIcons classname="text-amber-600" />
							Manual refund
						</span>
						<span className="text-sm font-bold text-amber-900">
							{formatCurrency(allocation.manualAmount)}
						</span>
					</div>
				)}
			</div>
			{hasManual && (
				<div className="text-[11px] text-slate-500">
					Part of this refund can’t be returned to a card automatically and will be processed manually by
					staff.
				</div>
			)}
		</div>
	);
}

function formatCardLabel(card: RefundCardDto): string {
	const brand =
		(card.cardBrand ?? 'Card').charAt(0).toUpperCase() + (card.cardBrand ?? 'card').slice(1).toLowerCase();
	const last4 = card.last4 ? ` •••• ${card.last4}` : '';
	return `${brand}${last4}`;
}

type HeroAccent = 'amber' | 'brand' | 'slate';

const HERO_ACCENT_STYLES: Record<
	HeroAccent,
	{
		iconBg: string;
		cardBg: string;
		label: string;
		amount: string;
		divider: string;
	}
> = {
	amber: {
		iconBg: 'bg-amber-100 text-amber-600',
		cardBg: 'bg-amber-50 border-amber-200',
		label: 'text-amber-600',
		amount: 'text-amber-900',
		divider: 'border-amber-200 text-amber-700',
	},
	// Brand-primary tinted hero (the default + credit cases). Colours live in CSS so the
	// brand variable can be used with transparency — see cancel-booking-modal-content.scss.
	brand: {
		iconBg: 'cancel-hero-icon--brand',
		cardBg: 'cancel-hero-card--brand',
		label: 'cancel-hero-label--brand',
		amount: 'cancel-hero-amount--brand',
		divider: 'cancel-hero-divider--brand',
	},
	slate: {
		iconBg: 'bg-slate-100 text-slate-600',
		cardBg: 'bg-slate-50 border-slate-200',
		label: 'text-slate-600',
		amount: 'text-slate-900',
		divider: 'border-slate-200 text-slate-700',
	},
};

function resolveHeroAccent(staffOverride: boolean, _forceCreditRefund: boolean, manualOnly: boolean): HeroAccent {
	if (staffOverride) return 'amber';
	if (manualOnly) return 'slate';
	return 'brand';
}

function resolveHeroIcon(staffOverride: boolean, forceCreditRefund: boolean, manualOnly: boolean): string {
	if (staffOverride) return 'edit';
	if (manualOnly) return 'support_agent';
	if (forceCreditRefund) return 'redeem';
	return 'check';
}

function resolveExpiryLabel(forceCreditRefund: boolean, expiryYears: number): string | null {
	if (!forceCreditRefund) return null;
	if (expiryYears === 1) return '1 Year';
	return `${expiryYears} Years`;
}

function resolveConfirmHeading(staffOverride: boolean, _forceCreditRefund: boolean, _manualOnly: boolean): string {
	if (staffOverride) return 'Review manual override';
	return 'Confirm cancellation';
}

function resolveConfirmSubheading(
	staffOverride: boolean,
	forceCreditRefund: boolean,
	manualOnly: boolean,
	isFerryBooking: boolean,
): string {
	if (staffOverride) return 'A custom refund amount has been applied by a manager.';
	if (manualOnly) {
		return store.isStaff
			? 'Staff will process this refund manually after the booking is cancelled.'
			: 'Staff will contact you to process this refund after the booking is cancelled.';
	}
	if (forceCreditRefund) return `A ${creditTermInline(isFerryBooking)} for the following amount will be issued.`;
	return 'Please review the refund total before proceeding.';
}

function resolveConfirmFootnote(
	_staffOverride: boolean,
	forceCreditRefund: boolean,
	manualOnly: boolean,
	isFerryBooking: boolean,
): string {
	if (manualOnly) {
		return store.isStaff
			? 'By confirming, the booking will be cancelled. The cancellation cannot be undone — process the refund manually via the usual workflow.'
			: 'By confirming, the booking will be cancelled. The cancellation cannot be undone — a staff member will be in touch to process the refund.';
	}
	if (forceCreditRefund) {
		return `By confirming, the booking will be cancelled and a ${creditTermInline(isFerryBooking)} issued. This cannot be undone.`;
	}
	return 'By confirming, the booking will be cancelled and the refund will be issued. This cannot be undone.';
}
